import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import QRCode from 'qrcode';
import './App.css';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const PROFILE_ID = 'default';
const PRINT_SERVER = 'https://print.gpslegacy.app';
const APP_URL = 'https://app.gpslegacy.app';

const FALLBACK_VERSES = [
  { text: "For I know the plans I have for you, declares the Lord, plans for welfare and not for evil, to give you a future and a hope.", ref: "Jeremiah 29:11" },
  { text: "Trust in the Lord with all your heart, and do not lean on your own understanding. In all your ways acknowledge him, and he will make straight your paths.", ref: "Proverbs 3:5-6" },
  { text: "I can do all things through him who strengthens me.", ref: "Philippians 4:13" },
  { text: "The Lord is my shepherd; I shall not want.", ref: "Psalm 23:1" },
  { text: "Be strong and courageous. Do not be frightened, and do not be dismayed, for the Lord your God is with you wherever you go.", ref: "Joshua 1:9" },
  { text: "And we know that for those who love God all things work together for good, for those who are called according to his purpose.", ref: "Romans 8:28" },
  { text: "She is clothed with strength and dignity, and she laughs without fear of the future.", ref: "Proverbs 31:25" },
  { text: "Come to me, all who labor and are heavy laden, and I will give you rest.", ref: "Matthew 11:28" },
  { text: "The Lord bless you and keep you; the Lord make his face shine on you and be gracious to you.", ref: "Numbers 6:24-25" },
  { text: "Give thanks to the Lord, for he is good, for his steadfast love endures forever.", ref: "Psalm 136:1" },
];

const ALL_INDICATORS = [
  { id: 'fragile', label: 'Fragile' },
  { id: 'this_side_up', label: 'This Side Up' },
  { id: 'keep_dry', label: 'Keep Dry' },
  { id: 'do_not_stack', label: 'Do Not Stack' },
  { id: 'heavy', label: 'Heavy' },
  { id: 'team_lift', label: 'Team Lift' },
  { id: 'electronics', label: 'Electronics' },
  { id: 'temp_sensitive', label: 'Temp Sensitive' },
  { id: 'liquids', label: 'Liquids' },
  { id: 'food', label: 'Food' },
  { id: 'medications', label: 'Medications' },
  { id: 'open_first', label: 'Open First' },
  { id: 'easy_access', label: 'Easy Access' },
  { id: 'sentimental', label: 'Sentimental' },
];

const INDICATOR_ICONS = {
  fragile: '🫧',
  this_side_up: '⬆️',
  keep_dry: '💧',
  do_not_stack: '🚫',
  heavy: '🏋️',
  team_lift: '👥',
  electronics: '💻',
  temp_sensitive: '🌡️',
  liquids: '🧴',
  food: '🍽️',
  medications: '💊',
  open_first: '⭐',
  easy_access: '🔓',
  sentimental: '💛',
};

function getOwnerPrefix(ownerName) {
  if (!ownerName || ownerName.trim() === '') return 'XX';
  return ownerName.trim().substring(0, 2).toUpperCase();
}

function formatBoxNumber(ownerName, boxNumber) {
  const prefix = getOwnerPrefix(ownerName);
  return `${prefix}-${String(boxNumber).padStart(3, '0')}`;
}

function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [savedItem, setSavedItem] = useState(null);
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHamburger, setShowHamburger] = useState(false);
  const [showVersePopup, setShowVersePopup] = useState(false);
  const [currentVerse, setCurrentVerse] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printItem, setPrintItem] = useState(null);
  const [printHandlingLabel, setPrintHandlingLabel] = useState(false);
  const [editingBox, setEditingBox] = useState(null);

  const [ownerName, setOwnerName] = useState('');
  const [legacyName, setLegacyName] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  const [savedProperties, setSavedProperties] = useState([]);
  const [savedProjects, setSavedProjects] = useState([]);
  const [savedZones, setSavedZones] = useState([]);
  const [savedOwners, setSavedOwners] = useState([]);

  const [property, setProperty] = useState('');
  const [project, setProject] = useState('');
  const [zone, setZone] = useState('');
  const [boxNumber, setBoxNumber] = useState('');
  const [contents, setContents] = useState('');
  const [storageLocation, setStorageLocation] = useState('');
  const [category, setCategory] = useState('');
  const [boxOwner, setBoxOwner] = useState('');
  const [indicators, setIndicators] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [primaryPhoto, setPrimaryPhoto] = useState(1);

  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  useEffect(() => {
    fetchItems();
    fetchProfile();
    fetchSavedOptions();
    checkDeepLink();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function checkDeepLink() {
    const hash = window.location.hash;
    if (hash.startsWith('#/box/')) {
      const boxId = hash.replace('#/box/', '');
      if (boxId) {
        localStorage.setItem('gps_pending_box', boxId);
        loadBoxById(boxId);
      }
    } else {
      const pendingBox = localStorage.getItem('gps_pending_box');
      if (pendingBox) {
        localStorage.removeItem('gps_pending_box');
        loadBoxById(pendingBox);
      }
    }
  }

  async function loadBoxById(id) {
    const { data } = await supabase.from('items').select('*').eq('id', id).single();
    if (data) {
      setSelectedBox(data);
      setActiveTab('detail');
    }
  }

  async function fetchVerseOfDay() {
    try {
      const verseIds = ['jer.29.11', 'pro.3.5-6', 'php.4.13', 'psa.23.1', 'jos.1.9', 'rom.8.28', 'pro.31.25', 'mat.11.28', 'num.6.24-25', 'psa.136.1'];
      const randomId = verseIds[Math.floor(Math.random() * verseIds.length)];
      const res = await fetch(`https://bible-api.com/${randomId}?translation=esv`);
      if (res.ok) {
        const data = await res.json();
        setCurrentVerse({ text: data.text.trim(), ref: data.reference });
      } else {
        setCurrentVerse(FALLBACK_VERSES[Math.floor(Math.random() * FALLBACK_VERSES.length)]);
      }
    } catch {
      setCurrentVerse(FALLBACK_VERSES[Math.floor(Math.random() * FALLBACK_VERSES.length)]);
    }
    setShowVersePopup(true);
  }

  async function fetchProfile() {
    const { data } = await supabase.from('profiles').select('*').eq('id', PROFILE_ID).single();
    if (data) {
      setOwnerName(data.owner_name || '');
      setLegacyName(data.legacy_name || '');
    }
  }

  async function saveProfile() {
    const { data: existing } = await supabase.from('profiles').select('id').eq('id', PROFILE_ID).single();
    if (existing) {
      await supabase.from('profiles').update({ owner_name: ownerName, legacy_name: legacyName }).eq('id', PROFILE_ID);
    } else {
      await supabase.from('profiles').insert([{ id: PROFILE_ID, owner_name: ownerName, legacy_name: legacyName }]);
    }
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2000);
  }

  async function fetchSavedOptions() {
    const { data } = await supabase.from('saved_options').select('*').order('created_at', { ascending: false });
    if (data) {
      setSavedProperties([...new Set(data.filter(d => d.type === 'property').map(d => d.value))]);
      setSavedProjects([...new Set(data.filter(d => d.type === 'project').map(d => d.value))]);
      setSavedZones([...new Set(data.filter(d => d.type === 'zone').map(d => d.value))]);
      setSavedOwners([...new Set(data.filter(d => d.type === 'box_owner').map(d => d.value))]);
    }
  }

  async function saveOption(type, value) {
    if (!value) return;
    const { data: existing } = await supabase.from('saved_options').select('id').eq('type', type).eq('value', value).single();
    if (!existing) await supabase.from('saved_options').insert([{ type, value }]);
  }

  async function fetchItems() {
    const { data } = await supabase.from('items').select('*').order('created_at', { ascending: false });
    if (data) setItems(data);
  }

  async function getNextBoxNumberForOwner(ownerName) {
    if (!ownerName || ownerName.trim() === '') {
      const { data } = await supabase.from('items').select('box_number');
      if (!data || data.length === 0) return 1;
      return Math.max(...data.map(d => d.box_number || 0)) + 1;
    }
    const { data } = await supabase.from('items').select('box_number').eq('whose_name', ownerName.trim());
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map(d => d.box_number || 0)) + 1;
  }

  async function uploadPhoto(file, index) {
    const fileName = `${Date.now()}-${index}-${file.name}`;
    const { data, error } = await supabase.storage.from('photos').upload(fileName, file);
    if (error) { console.error('Upload error:', error); return ''; }
    if (data) {
      const { data: urlData } = supabase.storage.from('photos').getPublicUrl(fileName);
      return urlData.publicUrl;
    }
    return '';
  }

  function handlePhotoSelect(e) {
    const files = Array.from(e.target.files).slice(0, 5);
    setPhotos(prev => [...prev, ...files].slice(0, 5));
    setPhotoPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))].slice(0, 5));
    setShowBottomSheet(false);
    setActiveTab('add');
  }

  function removePhoto(index) {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    if (primaryPhoto === index + 1) setPrimaryPhoto(1);
  }

  function toggleIndicator(id) {
    setIndicators(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }

  function startEdit(box) {
    setEditingBox(box);
    setProperty(box.property || '');
    setProject(box.project || '');
    setZone(box.zone || box.room || '');
    setBoxNumber(box.box_number || '');
    setContents(box.description || '');
    setStorageLocation(box.storage_location || box.location || '');
    setCategory(box.category || '');
    setBoxOwner(box.whose_name || '');
    setIndicators(box.indicators ? box.indicators.split(',').filter(Boolean) : []);
    setPhotos([]);
    setPhotoPreviews([]);
    setActiveTab('add');
  }

  function resetForm() {
    setProperty(''); setProject(''); setZone(''); setBoxNumber('');
    setContents(''); setStorageLocation(''); setCategory('');
    setBoxOwner(''); setIndicators([]); setPhotos([]); setPhotoPreviews([]);
    setPrimaryPhoto(1);
  }

  async function handleSubmit() {
    if (!property) return;
    setLoading(true);

    // EDIT MODE: update existing record
    if (editingBox) {
      const updates = {
        property, project, zone,
        description: contents,
        location: storageLocation,
        storage_location: storageLocation,
        room: zone,
        category,
        indicators: indicators.length > 0 ? indicators.join(',') : null,
      };
      const { data: updated } = await supabase
        .from('items').update(updates).eq('id', editingBox.id).select().single();
      if (updated) {
        setSelectedBox(updated);
        await saveOption('property', property);
        await saveOption('project', project);
        await saveOption('zone', zone);
        fetchSavedOptions();
        fetchItems();
      }
      setEditingBox(null);
      resetForm();
      setLoading(false);
      setActiveTab('detail');
      return;
    }

    // ADD MODE: original behavior, unchanged
    const autoBoxNumber = boxNumber || await getNextBoxNumberForOwner(boxOwner);

    const photoUrls = {};
    for (let i = 0; i < photos.length; i++) {
      const url = await uploadPhoto(photos[i], i + 1);
      photoUrls[`photo_url_${i + 1}`] = url;
    }

    const { data: newItem } = await supabase.from('items').insert([{
      property, project, zone,
      box_number: autoBoxNumber,
      box_name: boxOwner || property,
      description: contents,
      location: storageLocation,
      storage_location: storageLocation,
      room: zone,
      whose_name: boxOwner,
      category: category,
      indicators: indicators.length > 0 ? indicators.join(',') : null,
      primary_photo: primaryPhoto,
      ...photoUrls,
      created_at: new Date().toISOString()
    }]).select().single();

    if (newItem) {
      const qrUrl = `${APP_URL}/#/box/${newItem.id}`;
      const qr = await QRCode.toDataURL(qrUrl, { errorCorrectionLevel: 'M', width: 300 });
      await supabase.from('items').update({ qr_code: qr }).eq('id', newItem.id);
      const savedItemWithQR = { ...newItem, qr_code: qr };
      setSavedItem(savedItemWithQR);
      fetchItems();

      await saveOption('property', property);
      await saveOption('project', project);
      await saveOption('zone', zone);
      await saveOption('box_owner', boxOwner);
      fetchSavedOptions();
    }

    resetForm();
    setLoading(false);
    setActiveTab('success');
  }

  async function deleteBox(id) {
    await supabase.from('items').delete().eq('id', id);
    setShowDeleteConfirm(false);
    setSelectedBox(null);
    setActiveTab('storage');
    fetchItems();
  }

  function handlePrint(item) {
    setPrintItem(item);
    setShowPrintPreview(true);
  }

  async function triggerBrowserPrint() {
    if (!printItem) return;
    setPrinting(true);
    try {
      const itemIndicators = printItem.indicators
        ? printItem.indicators.split(',').filter(Boolean)
        : [];
      for (let i = 0; i < copies; i++) {
        await fetch(`${PRINT_SERVER}/print`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            qrCode: printItem.qr_code,
            boxName: printItem.box_name,
            boxNumber: formatBoxNumber(printItem.whose_name || ownerName, printItem.box_number),
            zone: printItem.zone || printItem.room || '',
            storageLocation: printItem.storage_location || printItem.location || '',
            category: printItem.category || '',
            description: printItem.description || '',
            property: printItem.property || '',
            ownerName: printItem.whose_name || ownerName || '',
            legacyName: legacyName || '',
            indicators: itemIndicators,
            printHandlingLabel: printHandlingLabel && itemIndicators.length > 0,
            date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          })
        });
      }
      alert(`✅ ${copies} label${copies > 1 ? 's' : ''} sent to printer!`);
      setShowPrintPreview(false);
    } catch (err) {
      alert('❌ Could not reach print server. Make sure it is running on MacKenzie (print.gpslegacy.app).');
    }
    setPrinting(false);
  }

  const getPrimaryPhoto = (item) => {
    const primary = item[`photo_url_${item.primary_photo || 1}`];
    if (primary) return primary;
    return item.photo_url_1 || item.photo_url_2 || item.photo_url_3 || item.photo_url_4 || item.photo_url_5 || null;
  };

  const getAllPhotos = (item) => [item.photo_url_1, item.photo_url_2, item.photo_url_3, item.photo_url_4, item.photo_url_5].filter(Boolean);

  const filteredItems = items.filter(item =>
    [item.property, item.project, item.zone, item.box_name, item.description, item.storage_location, item.location, item.room, item.whose_name, item.category]
      .some(field => field?.toLowerCase().includes(search.toLowerCase()))
  );

  const recentItems = items.slice(0, 3);

  const LabelPreview = ({ item }) => {
    const itemIndicators = item.indicators ? item.indicators.split(',').filter(Boolean) : [];
    return (
      <div className="label-preview">
        <div className="lp-owner">{item.whose_name || ownerName || '—'}</div>
        <div className="lp-date">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
        <div className="lp-qr"><img src={item.qr_code} alt="QR" /></div>
        <div className="lp-category">
          <span className="lp-label">Property</span>
          <span className="lp-value">{item.property}</span>
        </div>
        {item.category && (
          <div className="lp-category">
            <span className="lp-label">Category</span>
            <span className="lp-value">{item.category}</span>
          </div>
        )}
        {item.description && (
          <div className="lp-contents">
            <span className="lp-label">Contents</span>
            <span className="lp-value">{item.description}</span>
          </div>
        )}
        <div className="lp-location">
          <div>
            <span className="lp-label">Zone</span>
            <span className="lp-value">{item.zone || item.room || '—'}</span>
          </div>
          <div className="lp-divider" />
          <div>
            <span className="lp-label">Storage Location</span>
            <span className="lp-value">{item.storage_location || item.location || '—'}</span>
          </div>
        </div>
        {itemIndicators.length > 0 && (
          <div className="lp-indicators">
            <span className="lp-label">Handling</span>
            <div className="lp-indicator-pills">
              {itemIndicators.map(id => (
                <span key={id} className="lp-indicator-pill">
                  {INDICATOR_ICONS[id]} {ALL_INDICATORS.find(i => i.id === id)?.label || id}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="lp-boxnumber">
          <span className="lp-label">Box Number</span>
          <span className="lp-bignumber">{formatBoxNumber(item.whose_name || ownerName, item.box_number)}</span>
        </div>
        <div className="lp-legacy">
          Created with GPS-Legacy for<br />
          <strong>{legacyName || '_______________'}</strong>
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <header className="header">
        <button className="icon-btn" onClick={() => setShowHamburger(true)}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <line x1="2" y1="6" x2="20" y2="6" stroke="#0F2D23" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="2" y1="11" x2="20" y2="11" stroke="#0F2D23" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="2" y1="16" x2="20" y2="16" stroke="#0F2D23" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        <img
          src="/GPS - LEGACY.png"
          alt="GPS Legacy"
          className="header-logo"
          onClick={fetchVerseOfDay}
          style={{ cursor: 'pointer' }}
          title="Tap for a word of encouragement"
        />
        <div style={{ width: '32px' }} />
      </header>

      <main className="main">

        {activeTab === 'home' && (
          <>
            <section className="hero">
              <div className="hero-text">
                <h1>Organize Today.<br />Preserve Tomorrow.</h1>
                <p>Your home. Your stories. Your legacy.</p>
              </div>
            </section>

            <section className="stats-section">
              <div className="stats-card" onClick={() => setActiveTab('storage')} style={{ cursor: 'pointer' }}>
                <p className="stats-label">Your storage at a glance</p>
                <div className="stats-grid">
                  <div className="stat-box">
                    <p className="stat-title">My Boxes</p>
                    <p className="stat-number">{items.length}</p>
                  </div>
                  <div className="stat-box">
                    <p className="stat-title">Family Total</p>
                    <p className="stat-number">{items.length}</p>
                    <p className="stat-note">Legacy owner view</p>
                  </div>
                </div>
                <button className="download-btn" onClick={(e) => {
                  e.stopPropagation();
                  const csv = ['Box Number,Property,Zone,Contents,Storage Location,Owner',
                    ...items.map(i => `${formatBoxNumber(i.whose_name, i.box_number)},"${i.property || ''}","${i.zone || i.room || ''}","${i.description || ''}","${i.storage_location || i.location || ''}","${i.whose_name || ''}"`)
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'my-storage.csv'; a.click();
                }}>
                  ↓ Download my storage details
                </button>
              </div>
            </section>

            {recentItems.length > 0 && (
              <section className="section">
                <h2 className="section-title">Recently Added</h2>
                <div className="recent-list">
                  {recentItems.map(item => (
                    <div key={item.id} className="recent-item" onClick={() => { setSelectedBox(item); setActiveTab('detail'); }}>
                      <div className="recent-icon">
                        {getPrimaryPhoto(item)
                          ? <img src={getPrimaryPhoto(item)} alt={item.box_name} className="recent-thumb-img" />
                          : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                        }
                      </div>
                      <div className="recent-info">
                        <p className="recent-name">{item.whose_name || item.box_name || formatBoxNumber(item.whose_name, item.box_number)}</p>
                        <p className="recent-meta">{formatBoxNumber(item.whose_name, item.box_number)} · {item.storage_location || item.location || item.zone || '—'}</p>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B6B6B" strokeWidth="1.5"><path d="M9 18l6-6-6-6"/></svg>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="section">
              <h2 className="section-title">Coming Soon</h2>
              <div className="modules-list">
                <div className="module-item">
                  <div className="module-icon-tile">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  </div>
                  <div><p className="module-name">Heirloom Vault</p><p className="module-desc">Document keepsakes and the stories they carry</p></div>
                </div>
                <div className="module-item">
                  <div className="module-icon-tile">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                  </div>
                  <div><p className="module-name">Collection Cabinet</p><p className="module-desc">Track coins, cards, and collectibles</p></div>
                </div>
                <div className="module-item">
                  <div className="module-icon-tile">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </div>
                  <div><p className="module-name">Legacy Suite</p><p className="module-desc">All modules, one powerful bundle</p></div>
                </div>
              </div>
            </section>
          </>
        )}

        {activeTab === 'storage' && (
          <div className="storage-screen">
            <div className="screen-header">
              <button className="back-btn" onClick={() => setActiveTab('home')}>← Back</button>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 className="screen-title">My Storage</h2>
                  <p className="screen-subtitle">{items.length} box{items.length !== 1 ? 'es' : ''} catalogued</p>
                </div>
                <div className="view-toggle">
                  <button className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>⊞</button>
                  <button className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>☰</button>
                </div>
              </div>
              <input className="storage-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search boxes, zones, contents..." />
            </div>

            {filteredItems.length === 0 ? (
              <div className="empty-state">
                <p className="empty-icon">🗃️</p>
                <p className="empty-title">{search ? 'No results found' : 'No boxes yet'}</p>
                <p className="empty-desc">{search ? 'Try a different search term' : 'Tap the + button to add your first box'}</p>
                {!search && <button className="primary-btn" onClick={() => setShowBottomSheet(true)}>Add First Box</button>}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="box-grid">
                {filteredItems.map(item => (
                  <div key={item.id} className="box-card" onClick={() => { setSelectedBox(item); setActiveTab('detail'); }}>
                    <div className="box-thumbnail">
                      {getPrimaryPhoto(item) ? (
                        <img src={getPrimaryPhoto(item)} alt={item.box_name} className="box-thumb-img" />
                      ) : (
                        <div className="box-thumb-placeholder">📦</div>
                      )}
                    </div>
                    <div className="box-card-info">
                      <p className="box-number">{formatBoxNumber(item.whose_name, item.box_number)}</p>
                      <p className="box-card-name">{item.whose_name || item.box_name}</p>
                      <p className="box-card-project">{[item.property, item.project, item.zone].filter(Boolean).join(' › ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="box-list">
                {filteredItems.map(item => (
                  <div key={item.id} className="box-list-item" onClick={() => { setSelectedBox(item); setActiveTab('detail'); }}>
                    <div className="box-list-thumb">
                      {getPrimaryPhoto(item) ? (
                        <img src={getPrimaryPhoto(item)} alt={item.box_name} className="box-list-thumb-img" />
                      ) : (
                        <div className="box-list-thumb-placeholder">📦</div>
                      )}
                    </div>
                    <div className="box-list-info">
                      <p className="box-number">{formatBoxNumber(item.whose_name, item.box_number)}</p>
                      <p className="box-card-name">{item.whose_name || item.box_name}</p>
                      <p className="box-card-project">{[item.property, item.project, item.zone].filter(Boolean).join(' › ')}</p>
                      {item.zone && <p className="box-list-room">📍 {item.zone}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'detail' && selectedBox && (
          <div className="detail-screen">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="back-btn" onClick={() => { setSelectedBox(null); setActiveTab('storage'); window.location.hash = ''; }}>← Back</button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => startEdit(selectedBox)} style={{ background: 'transparent', border: '1px solid #A67C2E', color: '#A67C2E', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>✏️ Edit</button>
                <button className="delete-btn" onClick={() => setShowDeleteConfirm(true)}>🗑️ Delete</button>
              </div>
            </div>

            <div className="detail-photos">
              {getAllPhotos(selectedBox).length > 0 ? (
                <div className="photo-scroll">
                  {getAllPhotos(selectedBox).map((url, i) => (
                    <img key={i} src={url} alt={`Item ${i + 1}`} className="detail-photo" />
                  ))}
                </div>
              ) : (
                <div className="detail-photo-placeholder">📦</div>
              )}
            </div>

            <div className="detail-content">
              <div className="detail-breadcrumb">{[selectedBox.property, selectedBox.project, selectedBox.zone].filter(Boolean).join(' › ')}</div>
              <h2 className="detail-title">{formatBoxNumber(selectedBox.whose_name, selectedBox.box_number)}{selectedBox.whose_name ? ` — ${selectedBox.whose_name}` : ''}</h2>
              {selectedBox.whose_name && <p className="detail-whose">👤 {selectedBox.whose_name}</p>}
              {selectedBox.category && <p className="detail-whose">🏷️ {selectedBox.category}</p>}
              <p className="detail-location">📍 {selectedBox.storage_location || selectedBox.location}</p>
              {selectedBox.description && <p className="detail-description">{selectedBox.description}</p>}

              {selectedBox.qr_code && (
                <div className="qr-section">
                  <LabelPreview item={selectedBox} />
                  <div className="copies-row">
                    <label className="label-toggle">Copies:</label>
                    <input type="number" min="1" max="10" value={copies} onChange={e => setCopies(parseInt(e.target.value))} className="copies-input" />
                  </div>
                  <button className="print-btn" onClick={() => handlePrint(selectedBox)}>
                    🖨️ Print Label
                  </button>
                </div>
              )}
            </div>

            {showDeleteConfirm && (
              <>
                <div className="overlay" onClick={() => setShowDeleteConfirm(false)} />
                <div className="bottom-sheet">
                  <div className="bottom-sheet-handle" />
                  <h3 className="bottom-sheet-title">Delete this box?</h3>
                  <p style={{ textAlign: 'center', color: '#6B6B6B', fontSize: '13px', marginBottom: '20px', fontWeight: '300' }}>This cannot be undone.</p>
                  <button className="save-btn" style={{ background: '#6E2F2F' }} onClick={() => deleteBox(selectedBox.id)}>🗑️ Yes, Delete</button>
                  <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'add' && (
          <div className="add-screen">
            <div className="screen-header">
              <button className="back-btn" onClick={() => {
                if (editingBox) { setEditingBox(null); resetForm(); setActiveTab('detail'); }
                else { resetForm(); setActiveTab('home'); }
              }}>← Back</button>
              <h2 className="screen-title">{editingBox ? 'Edit Box' : 'Add New Box'}</h2>
              <p className="screen-subtitle">{editingBox ? 'Update box info' : 'Catalogue a box or bin'}</p>
            </div>

            {!editingBox && (
              <div className="photo-tiles-row">
                <div className="photo-tiles">
                  <button className="photo-tile" onClick={() => cameraRef.current.click()}>
                    <div className="photo-tile-icon">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    </div>
                    <span>Take photo</span>
                  </button>
                  <button className="photo-tile" onClick={() => uploadRef.current.click()}>
                    <div className="photo-tile-icon">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    </div>
                    <span>Choose files</span>
                  </button>
                </div>
              </div>
            )}

            {!editingBox && photoPreviews.length > 0 && (
              <div className="photo-preview-row">
                {photoPreviews.map((src, i) => (
                  <div key={i} className="preview-thumb-wrap">
                    <img src={src} alt={`Preview ${i + 1}`} className={`preview-thumb ${primaryPhoto === i + 1 ? 'primary-photo' : ''}`} onClick={() => setPrimaryPhoto(i + 1)} />
                    <button className="remove-photo-btn" onClick={() => removePhoto(i)}>✕</button>
                    {primaryPhoto === i + 1 && <div className="primary-badge">★</div>}
                  </div>
                ))}
                {photoPreviews.length < 5 && (
                  <button className="add-more-photo-btn" onClick={() => setShowBottomSheet(true)}>+ Add</button>
                )}
              </div>
            )}

            {editingBox && (
              <p style={{ fontSize: '12px', color: '#6B6B6B', textAlign: 'center', margin: '8px 0 16px', fontWeight: '300', fontStyle: 'italic' }}>
                📸 Photo editing coming in the next update
              </p>
            )}

            <div className="add-form">
              <div className="form-group">
                <label>Box Owner {editingBox && <span style={{fontSize:'10px',color:'#6B6B6B',fontWeight:'300'}}>(locked — tied to printed label)</span>}</label>
                <input tabIndex={1} list="owner-list" value={boxOwner} onChange={e => setBoxOwner(e.target.value)} placeholder="e.g. Madison, Bob's Office, Julia" disabled={!!editingBox} />
                <datalist id="owner-list">{savedOwners.map(p => <option key={p} value={p} />)}</datalist>
              </div>
              <div className="form-group">
                <label>Property *</label>
                <input tabIndex={2} list="property-list" value={property} onChange={e => setProperty(e.target.value)} placeholder="e.g. Our Family Home, Katy's House" />
                <datalist id="property-list">{savedProperties.map(p => <option key={p} value={p} />)}</datalist>
              </div>
              <div className="form-group">
                <label>Project</label>
                <input tabIndex={3} list="project-list" value={project} onChange={e => setProject(e.target.value)} placeholder="e.g. Long Term Storage, The Move" />
                <datalist id="project-list">{savedProjects.map(p => <option key={p} value={p} />)}</datalist>
              </div>
              <div className="form-group">
                <label>Zone</label>
                <input tabIndex={4} list="zone-list" value={zone} onChange={e => setZone(e.target.value)} placeholder="e.g. Kitchen, Master Bedroom, Garage" />
                <datalist id="zone-list">{savedZones.map(p => <option key={p} value={p} />)}</datalist>
              </div>
              <div className="form-group">
                <label>Category</label>
                <input tabIndex={5} value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Tech, Kitchen, Clothes" />
              </div>
              <div className="form-group">
                <label>Contents <span style={{ fontSize: '10px', color: '#6B6B6B', fontWeight: '300' }}>(one item per line, max 8)</span></label>
                <textarea
                  tabIndex={6}
                  value={contents}
                  onChange={e => {
                    const lines = e.target.value.split('\n');
                    if (lines.length <= 8) setContents(e.target.value);
                  }}
                  placeholder={"Laptop\nCords\nHeadphones"}
                  rows={4}
                  className="contents-textarea"
                />
              </div>
              <div className="form-group">
                <label>Storage Location</label>
                <input tabIndex={7} value={storageLocation} onChange={e => setStorageLocation(e.target.value)} placeholder="e.g. Garage Shelf A-2, Attic" />
              </div>
              <div className="form-group">
                <label>Box Number {editingBox && <span style={{fontSize:'10px',color:'#6B6B6B',fontWeight:'300'}}>(locked — tied to printed label)</span>}</label>
                <input tabIndex={8} type="number" value={boxNumber} onChange={e => setBoxNumber(e.target.value)} placeholder="Auto-assigned if left blank" disabled={!!editingBox} />
              </div>
              <div className="form-group">
                <label>Special Indicators</label>
                <p style={{ fontSize: '11px', color: '#6B6B6B', marginTop: '-2px', fontWeight: '300' }}>Tap to select. Selected indicators print on the label.</p>
                <div className="indicators-grid">
                  {ALL_INDICATORS.map(ind => (
                    <button key={ind.id} type="button" tabIndex={-1}
                      className={`indicator-pill ${indicators.includes(ind.id) ? 'indicator-pill-active' : ''}`}
                      onClick={() => toggleIndicator(ind.id)}>
                      <span className="indicator-pill-icon">{INDICATOR_ICONS[ind.id]}</span>
                      <span>{ind.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button className="save-btn" onClick={handleSubmit} disabled={loading || !property}>
                {loading ? 'Saving...' : editingBox ? '💾 Save Changes' : '💾 Save Box + Generate QR Code'}
              </button>
              <button className="cancel-btn" onClick={() => {
                if (editingBox) { setEditingBox(null); resetForm(); setActiveTab('detail'); }
                else { resetForm(); setActiveTab('home'); }
              }}>Cancel</button>
            </div>
          </div>
        )}

        {activeTab === 'success' && savedItem && (
          <div className="success-screen">
            <button className="back-btn" style={{ alignSelf: 'flex-start' }} onClick={() => { setSavedItem(null); setActiveTab('home'); }}>← Back</button>
            <div className="success-icon">✅</div>
            <h2 className="success-title">Box Saved!</h2>
            <p className="success-subtitle">{savedItem.property} · {formatBoxNumber(savedItem.whose_name, savedItem.box_number)}</p>

            {savedItem.qr_code && (
              <div className="success-qr" style={{ width: '100%' }}>
                <LabelPreview item={savedItem} />
                <div className="copies-row">
                  <label className="label-toggle">Copies:</label>
                  <input type="number" min="1" max="10" value={copies} onChange={e => setCopies(parseInt(e.target.value))} className="copies-input" />
                </div>
                <button className="print-btn-large" onClick={() => handlePrint(savedItem)}>
                  🖨️ Print Label
                </button>
              </div>
            )}

            <button className="primary-btn" style={{ width: '100%', marginTop: '8px' }} onClick={() => { setSavedItem(null); setActiveTab('storage'); }}>📦 Go to My Storage</button>
            <button className="cancel-btn" onClick={() => { setSavedItem(null); setActiveTab('home'); }}>Back to Home</button>
          </div>
        )}

        {activeTab === 'search' && (
          <div className="search-screen">
            <div className="screen-header">
              <button className="back-btn" onClick={() => setActiveTab('home')}>← Back</button>
              <h2 className="screen-title">Search</h2>
              <p className="screen-subtitle">Find anything without opening a box</p>
            </div>
            <input className="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by property, zone, contents, location..." autoFocus />
            <div className="search-results">
              {search === '' ? (
                <p className="search-hint">Start typing to find your items</p>
              ) : filteredItems.length === 0 ? (
                <p className="search-hint">No results found for "{search}"</p>
              ) : (
                filteredItems.map(item => (
                  <div key={item.id} className="search-result-item" onClick={() => { setSelectedBox(item); setActiveTab('detail'); }}>
                    <div className="search-thumb">
                      {getPrimaryPhoto(item) ? (
                        <img src={getPrimaryPhoto(item)} alt={item.box_name} className="search-thumb-img" />
                      ) : (
                        <div className="search-thumb-placeholder">📦</div>
                      )}
                    </div>
                    <div className="search-result-info">
                      <p className="search-result-name">{item.whose_name || item.box_name}</p>
                      <p className="search-result-meta">{formatBoxNumber(item.whose_name, item.box_number)} · {[item.property, item.project, item.zone].filter(Boolean).join(' › ')}</p>
                      <p className="search-result-location">📍 {item.storage_location || item.location}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="add-screen">
            <div className="screen-header">
              <button className="back-btn" onClick={() => setActiveTab('home')}>← Back</button>
              <h2 className="screen-title">My Profile</h2>
              <p className="screen-subtitle">Your GPS Legacy identity</p>
            </div>
            <div className="add-form">
              <div className="form-group">
                <label>Owner Name</label>
                <input value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="e.g. Kristina Solara" />
              </div>
              <div className="form-group">
                <label>Legacy Name</label>
                <input value={legacyName} onChange={e => setLegacyName(e.target.value)} placeholder="e.g. The Solara Family, ABC Industries" />
              </div>
              <p style={{ fontSize: '11px', color: '#6B6B6B', marginTop: '-8px', fontWeight: '300' }}>Appears on labels as "Created with GPS-Legacy for [name]"</p>
              <button className="save-btn" onClick={saveProfile}>{profileSaved ? '✅ Saved!' : '💾 Save Profile'}</button>
            </div>
          </div>
        )}

      </main>

      <input type="file" accept="image/*" capture="environment" ref={cameraRef} style={{ display: 'none' }} onChange={handlePhotoSelect} />
      <input type="file" accept="image/*" multiple ref={uploadRef} style={{ display: 'none' }} onChange={handlePhotoSelect} />

      {showBottomSheet && (
        <>
          <div className="overlay" onClick={() => setShowBottomSheet(false)} />
          <div className="bottom-sheet">
            <div className="bottom-sheet-handle" />
            <h3 className="bottom-sheet-title">Add Photos</h3>
            <div className="bottom-sheet-tiles">
              <button className="sheet-tile" onClick={() => cameraRef.current.click()}>
                <div className="sheet-tile-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                </div>
                <span>Take Photo</span>
              </button>
              <button className="sheet-tile" onClick={() => uploadRef.current.click()}>
                <div className="sheet-tile-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                </div>
                <span>Choose Files</span>
              </button>
            </div>
            <button className="sheet-cancel" onClick={() => { setShowBottomSheet(false); if (activeTab !== 'add') setActiveTab('add'); }}>Skip — No Photos</button>
          </div>
        </>
      )}

      {showHamburger && (
        <>
          <div className="overlay" onClick={() => setShowHamburger(false)} />
          <div className="side-menu">
            <div className="side-menu-brand">
              <p className="side-menu-brand-name">GPS Legacy</p>
              <p className="side-menu-brand-tagline">Finding What Matters</p>
            </div>
            <div className="side-menu-header">
              <div className="side-menu-user">
                <div className="side-menu-avatar">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div>
                  <p className="side-menu-name">{ownerName || 'Solara'}</p>
                  <p className="side-menu-email">graciouspeacestudio@gmail.com</p>
                </div>
              </div>
              <div className="side-menu-tier">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" stroke="#fff" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                <span>Grow</span>
              </div>
            </div>

            <div className="side-menu-body">
              <p className="side-menu-section-label">Menu</p>
              <button className="side-menu-item" onClick={() => { setActiveTab('home'); setShowHamburger(false); }}>
                <div className="side-menu-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
                <span>Home</span>
              </button>
              <button className="side-menu-item" onClick={() => { setActiveTab('storage'); setShowHamburger(false); }}>
                <div className="side-menu-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg></div>
                <span>My Storage</span>
              </button>
              <button className="side-menu-item" onClick={() => { setActiveTab('profile'); setShowHamburger(false); }}>
                <div className="side-menu-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
                <span>My Profile</span>
              </button>
              <button className="side-menu-item">
                <div className="side-menu-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg></div>
                <span>App Settings</span>
              </button>

              <div className="side-menu-divider" />
              <p className="side-menu-section-label">Modules</p>

              {[
                { label: 'Heirloom Vault', icon: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/> },
                { label: 'Collection Cabinet', icon: <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></> },
                { label: 'Recipe Book', icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></> },
                { label: 'Asset Vault', icon: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></> },
                { label: 'Good Steward', icon: <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></> },
                { label: 'Home Improvement', icon: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></> },
                { label: 'Legacy Suite', icon: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/> },
                { label: 'Estate Vault', icon: <><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></> },
              ].map(({ label, icon }) => (
                <div key={label} className="side-menu-item side-menu-item-locked">
                  <div className="side-menu-item-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="1.5">{icon}</svg>
                  </div>
                  <span style={{ flex: 1 }}>{label}</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6B6B6B" strokeWidth="1.5"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                </div>
              ))}
            </div>

            <div className="side-menu-footer">
              <button className="side-menu-signout">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E74C3C" strokeWidth="1.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </>
      )}

      {showPrintPreview && printItem && (
        <>
          <div className="overlay" onClick={() => setShowPrintPreview(false)} />
          <div className="print-preview-sheet">
            <div className="bottom-sheet-handle" />
            <h3 className="bottom-sheet-title">Label Preview</h3>
            <div className="print-preview-label">
              <LabelPreview item={printItem} />
            </div>
            <div className="copies-row" style={{ padding: '0 16px', marginBottom: '12px' }}>
              <label className="label-toggle">Copies:</label>
              <input type="number" min="1" max="10" value={copies} onChange={e => setCopies(parseInt(e.target.value))} className="copies-input" />
            </div>
            {printItem.indicators && printItem.indicators.length > 0 && (
              <div style={{ padding: '0 16px', marginBottom: '12px' }}>
                <label className="label-toggle" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={printHandlingLabel}
                    onChange={e => setPrintHandlingLabel(e.target.checked)}
                  />
                  Print additional handling label
                </label>
              </div>
            )}
            <div style={{ padding: '0 16px' }}>
              <button className="save-btn" onClick={triggerBrowserPrint} disabled={printing}>
                {printing ? '🖨️ Sending...' : '🖨️ Send to Printer'}
              </button>
              <button className="cancel-btn" onClick={() => setShowPrintPreview(false)}>Close</button>
            </div>
          </div>
        </>
      )}

      {showVersePopup && currentVerse && (
        <>
          <div className="overlay" onClick={() => setShowVersePopup(false)} />
          <div className="verse-popup-overlay">
            <div className="verse-popup">
              <div className="verse-popup-line-top" />
              <div className="verse-popup-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="15" stroke="#C9984A" strokeWidth="1"/>
                  <line x1="16" y1="9" x2="16" y2="23" stroke="#C9984A" strokeWidth="1.5"/>
                  <line x1="10" y1="14" x2="22" y2="14" stroke="#C9984A" strokeWidth="1.5"/>
                  <path d="M8 20 Q16 25 24 20" stroke="#8F927D" strokeWidth="1" fill="none"/>
                </svg>
              </div>
              <p className="verse-popup-label">A word for you</p>
              <div className="verse-popup-divider" />
              <p className="verse-popup-text">"{currentVerse.text}"</p>
              <div className="verse-popup-divider" />
              <p className="verse-popup-ref">{currentVerse.ref} — ESV</p>
              <div className="verse-popup-divider" />
              <button className="verse-popup-close" onClick={() => setShowVersePopup(false)}>✦ &nbsp; close &nbsp; ✦</button>
              <div className="verse-popup-line-bottom" />
            </div>
          </div>
        </>
      )}

      <nav className="bottom-nav">
        <button className={`nav-btn ${activeTab === 'home' ? 'active' : ''}`} onClick={() => setActiveTab('home')}>
          <div className="nav-icon-tile">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <span>HOME</span>
        </button>
        <button className={`nav-btn ${activeTab === 'storage' ? 'active' : ''}`} onClick={() => setActiveTab('storage')}>
          <div className="nav-icon-tile">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          </div>
          <span>STORAGE</span>
        </button>
        <button className="nav-btn nav-center" onClick={() => setShowBottomSheet(true)}>
          <div className="nav-center-inner">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#A67C2E" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </div>
          <span>ADD</span>
        </button>
        <button className={`nav-btn ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>
          <div className="nav-icon-tile">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <span>SEARCH</span>
        </button>
        <button className={`nav-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
          <div className="nav-icon-tile">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <span>PROFILE</span>
        </button>
      </nav>
    </div>
  );
}

export default App;